import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { Loader2, RefreshCw, BrainCircuit, TrendingUp, Activity, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const confColor = (c) =>
  c === 'Alto' ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10'
  : c === 'Medio' ? 'text-amber-400 border-amber-500/40 bg-amber-500/10'
  : 'text-slate-400 border-slate-500/40 bg-slate-500/10';

const AdminAnalista = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [track, setTrack] = useState(null);

  const fetchTrack = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/analista/track-record`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTrack(res.data);
    } catch (e) { /* silencioso */ }
  }, [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analista/numero-del-dia`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      fetchTrack();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al calcular el número del día');
    } finally {
      setLoading(false);
    }
  }, [token, fetchTrack]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <AdminLayout title="Analista Avanzado" subtitle="Número general recomendado del día — todas las quinielas">
      <div className="flex justify-end mb-4">
        <Button onClick={fetchData} disabled={loading} className="bg-indigo-600 hover:bg-indigo-500" data-testid="recalcular-btn">
          {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
          Recalcular
        </Button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 size={40} className="text-indigo-400 animate-spin" />
        </div>
      ) : data ? (
        <div className="space-y-6" data-testid="analista-result">
          {/* Número recomendado */}
          <Card className="bg-gradient-to-br from-indigo-950 via-blue-950 to-slate-900 border-2 border-indigo-500/40">
            <CardContent className="p-8 text-center">
              <div className="flex items-center justify-center gap-2 text-indigo-300 mb-4">
                <BrainCircuit size={20} />
                <span className="uppercase tracking-widest text-xs font-bold">Número General Recomendado</span>
              </div>
              <div className="mx-auto w-40 h-40 rounded-3xl bg-gradient-to-br from-indigo-500 to-blue-700 flex items-center justify-center shadow-2xl shadow-indigo-700/50" data-testid="numero-recomendado">
                <span className="text-white font-black text-7xl">{String(data.numero_recomendado).padStart(2, '0')}</span>
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
                <div className="text-center">
                  <div className="text-3xl font-black text-white">{data.score}<span className="text-lg text-slate-400">/100</span></div>
                  <div className="text-slate-500 text-xs uppercase">Puntuación</div>
                </div>
                <div className={`px-4 py-2 rounded-xl border font-bold ${confColor(data.confianza)}`} data-testid="confianza">
                  Confianza: {data.confianza}
                  <span className="block text-[10px] font-normal opacity-70">z-score {data.z_score}</span>
                </div>
              </div>
              <div className="mt-5 text-left max-w-md mx-auto">
                <p className="text-indigo-300/70 text-xs uppercase font-bold mb-2">Razón de la recomendación</p>
                <ul className="space-y-1">
                  {data.razones?.map((r, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                      <TrendingUp size={14} className="text-emerald-400 shrink-0" /> {r}
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Aciertos reales del metodo (backtest 30 dias) */}
          {data.real_backtest?.days_tested > 0 && (
            <Card className="bg-gradient-to-br from-emerald-950/60 to-gray-900 border border-emerald-600/30" data-testid="analista-backtest">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Activity size={16} className="text-emerald-400" />
                  <h3 className="text-white font-bold">Aciertos REALES del método — backtest {data.real_backtest.days_tested} días</h3>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="p-3 rounded-xl bg-black/30 text-center">
                    <div className="text-emerald-400 font-black text-2xl">{data.real_backtest.hit_rate}%</div>
                    <div className="text-slate-500 text-[10px] uppercase">Acertó en alguna lotería</div>
                  </div>
                  <div className="p-3 rounded-xl bg-black/30 text-center">
                    <div className="text-white font-black text-2xl">{data.real_backtest.hits}/{data.real_backtest.days_tested}</div>
                    <div className="text-slate-500 text-[10px] uppercase">Días con acierto</div>
                  </div>
                  <div className="p-3 rounded-xl bg-black/30 text-center">
                    <div className="text-slate-400 font-black text-2xl">{data.real_backtest.baseline_azar}%</div>
                    <div className="text-slate-500 text-[10px] uppercase">Baseline azar</div>
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap mb-3">
                  {data.real_backtest.series?.map((h, i) => (
                    <span key={i} className={`w-5 h-5 rounded flex items-center justify-center text-[9px] font-black ${
                      h ? 'bg-emerald-500/80 text-white' : 'bg-slate-700/70 text-slate-400'
                    }`}>{h ? '✓' : '✗'}</span>
                  ))}
                </div>
                <div className="space-y-1">
                  {data.real_backtest.detail?.slice(0, 7).map((d) => (
                    <div key={d.date} className="flex items-center gap-2 text-xs">
                      <span className="text-slate-500 w-20">{d.date?.slice(5)}</span>
                      <span className="text-white font-bold w-8">{String(d.number).padStart(2, '0')}</span>
                      {d.hit ? (
                        <span className="text-emerald-400 flex items-center gap-1 truncate">
                          <CheckCircle2 size={12} /> {d.hit_lotteries.join(', ')}
                        </span>
                      ) : (
                        <span className="text-slate-600">Sin acierto</span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Track record de picks guardados */}
          {track?.summary?.total_days > 0 && (
            <Card className="bg-gray-900 border-gray-800" data-testid="analista-track-record">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-bold">Track record de recomendaciones</h3>
                  <span className="text-sm">
                    <strong className="text-emerald-400">{track.summary.total_hits}/{track.summary.total_days}</strong>
                    <span className="text-slate-500"> días con acierto ({track.summary.hit_rate}%)</span>
                  </span>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {track.history.map((h) => (
                    <div key={h.date} className={`px-2 py-1 rounded-lg border text-[10px] ${
                      !h.verified || h.in_progress
                        ? (h.hit ? 'bg-emerald-900/50 border-emerald-500/40 text-emerald-300' : 'bg-slate-800/60 border-slate-600/40 text-slate-400')
                      : h.hit ? 'bg-emerald-900/50 border-emerald-500/40 text-emerald-300'
                      : 'bg-rose-950/40 border-rose-700/30 text-rose-300/80'
                    }`}>
                      <div className="font-bold">{h.date?.slice(5)} · {String(h.number).padStart(2, '0')}</div>
                      <div>
                        {h.hit ? `✓ ${h.hit_lotteries?.slice(0, 2).join(', ')}`
                          : (!h.verified || h.in_progress) ? 'En curso' : '✗'}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top 10 */}
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-5">
              <h3 className="text-white font-bold mb-4">Top 10 con mayor puntuación</h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {data.top_10?.map((t, i) => (
                  <div key={t.number} className={`p-3 rounded-xl text-center border ${i === 0 ? 'border-indigo-500/50 bg-indigo-500/10' : 'border-gray-700 bg-gray-800/50'}`} data-testid={`top-${t.number}`}>
                    <div className="text-[10px] text-slate-500">#{i + 1}</div>
                    <div className="text-white font-black text-2xl my-1">{String(t.number).padStart(2, '0')}</div>
                    <div className="text-indigo-300 font-bold text-sm">{t.score}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Meta */}
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-5">
              <h3 className="text-white font-bold mb-3">Detalle del análisis</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                <div><span className="text-slate-500 block text-xs">Loterías analizadas</span><span className="text-white font-bold">{data.meta?.loterias_analizadas}</span></div>
                <div><span className="text-slate-500 block text-xs">Sorteos totales</span><span className="text-white font-bold">{data.meta?.sorteos_totales?.toLocaleString()}</span></div>
                <div><span className="text-slate-500 block text-xs">Posiciones analizadas</span><span className="text-white font-bold">{data.meta?.posiciones_totales?.toLocaleString()}</span></div>
                <div className="col-span-2 sm:col-span-3"><span className="text-slate-500 block text-xs">Excluidos (último sorteo)</span><span className="text-slate-300 text-xs">{data.meta?.excluidos_ultimo_sorteo?.length ? data.meta.excluidos_ultimo_sorteo.map(n => String(n).padStart(2, '0')).join(', ') : 'ninguno (se incluyen todos los números)'}</span></div>
              </div>
              <p className="text-slate-500 text-[11px] mt-4 leading-relaxed">
                Ensemble estadístico: frecuencia ponderada por recencia, frecuencia reciente e histórica, momentum, atraso (gap)
                y coincidencia entre loterías. Los sorteos son aleatorios; esta es la mejor selección estadística, no una garantía de acierto.
              </p>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="text-center text-slate-500 py-16">No hay datos disponibles.</div>
      )}
    </AdminLayout>
  );
};

export default AdminAnalista;
